import React, { useEffect, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import withQueryParams from "src/hocs/withQueryParams"
import {
  compose,
  withProps,
  withStateHandlers,
  withPropsOnChange,
} from "recompact"
import { connect } from "react-redux"
import { Box } from "@infotech/uikit-grid"

import DeletedIcon from "src/icons/deleted.svg"

import planResources from "src/hocs/planResources"
import resources from "src/hocs/resources"

import { TrashIcon, AddIcon, LoaderIcon } from "@infotech/uikit-icons"
import { Subtitle1, Hint } from "@infotech/uikit-text"
import { CleanButton } from "@infotech/uikit-button"
import Spin from "@infotech/uikit-spin"
import Select, { components } from "@infotech/uikit-select"

import { selectCurrentList } from "src/engine/modules/lists/selectors"
import { readList } from "src/engine/modules/lists/actions/creators/read"
import { updateList } from "src/engine/modules/lists/actions/creators/update"
import { selectRoles } from "src/engine/modules/currentUser"

import includes from "lodash/includes"
import get from "lodash/get"
import debounce from "lodash/debounce"

import { ADMIN } from "src/constants/knownRoles"
import GroupedResources from "src/components/GroupedResources"

import { queryParams, LIST_NAME, getBasicUrl, getUpdateUrl } from "./helper"

import styles from "./styles.css"

const ResourceOption = ({ data, ...props }) => (
  <components.Option
    data={data}
    {...props}
    getStyles={componentName => ({
      ...props.getStyles(componentName, props),
      display: `flex`,
      alignItems: `center`,
    })}
  >
    {data.resource.deleteDate ? (
      <>
        <Box loose mr="2md" flex align="center">
          <DeletedIcon className={styles.DeletedIcon} />
        </Box>
        <span
          style={{
            // В getStyles color не работает из-за бага в uikit
            // Поэтому приходится делать через span
            color: `#7f7f7f`,
          }}
        >
          {data.label}
        </span>
      </>
    ) : (
      data.label
    )}
  </components.Option>
)

// TODO: change updateList logic after back fix plan/resources list CRUD

const enhancer = compose(
  withQueryParams(),
  connect(state => ({
    canSeeDeletedResources: selectRoles(state)[ADMIN] || false,
  })),
  withStateHandlers(
    {
      cumulative: true,
      query: {
        limit: 30,
        offset: 0,
        // query есть, но нет
      },
    },
    {
      loadNextResources: ({ query: { limit, ...rest } }) => () => ({
        query: {
          limit: limit + 30,
          ...rest,
        },
      }),
      resetResources: ({ query }) => () => ({
        query: {
          ...query,
          limit: 30,
        },
      }),
      changeSearch: ({ query }) => string => ({
        query: {
          ...query,
          limit: 30,
          ...(string ? { query: `name=="*${string}*"` } : null),
        },
      }),
    },
  ),
  // Добавление фильтра удаленных ресурсов по условию
  withPropsOnChange(
    [`canSeeDeletedResources`, `query`],
    ({ canSeeDeletedResources, query }) => ({
      query: canSeeDeletedResources
        ? query
        : {
            ...query,
            query: `${query.query ? `${query.query};` : ``}deleteDate==null`,
          },
    }),
  ),
  resources({ to: `resourcesAvailable` }),
  planResources({ to: `resourcesWithEvents`, view: `WITH_EVENTS` }),
  withProps(({ resourcesAvailable, resourcesWithEvents }) => ({
    isAnyResourcePending:
      resourcesAvailable.pending || resourcesWithEvents.pending,
  })),
  // # here was queryApi
  connect(
    state => ({
      planResources: selectCurrentList(state, LIST_NAME),
    }),
    {
      readList,
      updateList,
    },
  ),
)

const PlanResourcesList = props => {
  const {
    planId,
    isAnyResourcePending,
    readList,
    updateList,
    resourcesAvailable,
    resourcesWithEvents,
    planResources,
    loadNextResources,
    resetResources,
    changeSearch,
    isDisabled,
    dataForTabs,
  } = props
  const { plan } = dataForTabs
  useEffect(() => {
    readList({
      basicUrl: getBasicUrl(planId),
      name: LIST_NAME,
      query: queryParams(`IN_PLAN`),
    })
    // }
  }, [planId])
  const planResourcesUuids = get(planResources, `data`, []).map(
    item => item.uuid,
  )

  const changeSearchDebounced = useCallback(debounce(changeSearch, 500), [
    changeSearch,
  ])

  // Между загрузками ресурсов массив обнуляется
  // В этот момент мы возвращаем тот что был до загрузки
  const ref = useRef([])
  useEffect(() => {
    if (resourcesAvailable.data.length) {
      ref.current = resourcesAvailable.data
    }
  }, [resourcesAvailable.data.length])

  const planResourcesOptions = ref.current
    .filter(item => !includes(planResourcesUuids, item.uuid))
    .map(item => ({
      value: item.uuid,
      label: item.name,
      resource: item,
    }))
  const resourcesOutPlan = get(resourcesWithEvents, `data`, []).filter(
    item => !includes(planResourcesUuids, item.uuid),
  )
  return (
    <Box loose>
      {get(resourcesWithEvents, `pending`) || get(plan, `pending`) ? (
        <Box
          flex
          className={styles.LoaderWrapper}
          align="center"
          justify="center"
        >
          <Spin duration="fast">
            <LoaderIcon color="#2880ff" />
          </Spin>
        </Box>
      ) : (
        <Box flex column borderContent>
          <Box loose flex>
            <Box loose className={styles.ResourcesColumns}>
              <Box loose p="2md" flex className={styles.ResourcesColumnsHeader}>
                {planResources && planResources.data && (
                  <Subtitle1>
                    Ресурсы в плане (
                    {planResources.data.length > 99
                      ? `+99`
                      : planResources.data.length}
                    )
                  </Subtitle1>
                )}
                {/* TODO: wait back for that */}
                {/* <Box */}
                {/*  loose */}
                {/*  className={cn( */}
                {/*    styles.InPlanExclude, */}
                {/*    get(planResources, `data`, []).length > 0 */}
                {/*      ? styles.InPlanExcludeActive */}
                {/*      : styles.InPlanExcludeDisabled, */}
                {/*  )} */}
                {/* > */}
                {/*  Исключить все */}
                {/* </Box> */}
              </Box>
              {planResources && planResources.data && (
                <>
                  <Box pl="2md" pr="2md">
                    <Select
                      isSearchable
                      cacheOptions
                      placeholder="Введите наименование ресурса для поиска"
                      disabled={isAnyResourcePending || isDisabled}
                      isLoading={isAnyResourcePending}
                      options={planResourcesOptions}
                      value={null}
                      onChange={({ target: { value: option } }) =>
                        updateList({
                          basicUrl: getUpdateUrl(planId),
                          name: LIST_NAME,
                          item: option.resource,
                          updateType: `update`,
                          newItem: {
                            ...plan.data,
                            accessibleResources: planResourcesUuids.concat(
                              option.value,
                            ),
                          },
                        })
                      }
                      components={{ Option: ResourceOption }}
                      onMenuScrollToBottom={loadNextResources}
                      onMenuClose={resetResources}
                      onInputChange={changeSearchDebounced}
                    />
                  </Box>
                  <Box loose pt="1md" className={styles.GroupResourcesWrapper}>
                    <GroupedResources
                      resources={planResources ? planResources.data : []}
                      onButtonClick={removeItem => {
                        const removeUuidData = planResourcesUuids.filter(
                          resource => resource !== removeItem.uuid,
                        )
                        updateList({
                          basicUrl: getUpdateUrl(planId),
                          name: LIST_NAME,
                          item: removeItem,
                          updateType: `delete`,
                          newItem: {
                            ...plan.data,
                            accessibleResources: removeUuidData,
                          },
                        })
                      }}
                      Button={({ onClick }) => (
                        <CleanButton
                          className={styles.ItemButton}
                          icon={TrashIcon}
                          onClick={onClick}
                        />
                      )}
                      isDisabled={isDisabled}
                    />
                  </Box>
                </>
              )}
            </Box>
            <Box className={styles.ResourcesColumns}>
              <Box loose p="2md" flex className={styles.ResourcesColumnsHeader}>
                <Box loose pl="2md" pb="2md" w="250px">
                  <Subtitle1>
                    Дополнительные ресурсы (
                    {resourcesOutPlan.length > 99
                      ? `+99`
                      : resourcesOutPlan.length}
                    )
                  </Subtitle1>
                  <Hint style={{ color: `#a1a1a1` }}>
                    Ресурсы, которые еще не добавлены в план, но задействованы в
                    событиях плана
                  </Hint>
                </Box>
                {/* TODO: wait back for that */}
                {/* <Box */}
                {/*  loose */}
                {/*  className={cn( */}
                {/*    styles.InPlanExclude, */}
                {/*    resourcesOutPlan.length > 0 */}
                {/*      ? styles.InPlanExcludeActive */}
                {/*      : styles.InPlanExcludeDisabled, */}
                {/*  )} */}
                {/* > */}
                {/*  Добавить все */}
                {/* </Box> */}
              </Box>
              <Box loose pt="1md" className={styles.GroupResourcesWrapper}>
                <GroupedResources
                  resources={resourcesOutPlan}
                  onButtonClick={resource =>
                    updateList({
                      basicUrl: getUpdateUrl(planId),
                      name: LIST_NAME,
                      item: resource,
                      updateType: `update`,
                      newItem: {
                        ...plan.data,
                        accessibleResources: planResourcesUuids.concat(
                          resource.uuid,
                        ),
                      },
                    })
                  }
                  Button={({ onClick }) => (
                    <CleanButton
                      className={styles.ItemButton}
                      icon={AddIcon}
                      type="button"
                      onClick={onClick}
                    />
                  )}
                  isDisabled={isDisabled}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

PlanResourcesList.propTypes = {
  planId: PropTypes.string.isRequired,
  isAnyResourcePending: PropTypes.bool.isRequired,
  readList: PropTypes.func.isRequired,
  updateList: PropTypes.func.isRequired,
  resourcesAvailable: PropTypes.shape({
    data: PropTypes.arrayOf({
      name: PropTypes.string,
      uuid: PropTypes.string,
    }),
  }).isRequired,
  plan: PropTypes.shape({
    pending: PropTypes.bool,
    data: PropTypes.arrayOf({
      name: PropTypes.string,
    }),
  }).isRequired,
  resourcesWithEvents: PropTypes.shape({
    data: PropTypes.arrayOf({
      name: PropTypes.string,
      uuid: PropTypes.string,
    }),
  }).isRequired,
  planResources: PropTypes.shape({
    data: PropTypes.arrayOf({
      name: PropTypes.string,
      uuid: PropTypes.string,
    }),
  }).isRequired,
  isDisabled: PropTypes.bool.isRequired,
}

export default enhancer(PlanResourcesList)
